import './filter-drawer.scss';
import { gsap } from 'gsap';
import { CustomEase } from 'gsap/CustomEase';

gsap.registerPlugin( CustomEase );

/**
 * Filter drawer — gespiegeld aan p-header (Osmo underlay slide-pattern).
 *
 * - [data-shell] glijden naar links om het panel rechts te onthullen
 * - dezelfde dark-overlay + corners + borders van p-header krijgen een
 *   spiegel-animatie zodat het visueel 1:1 op het hoofdmenu lijkt
 * - p-header bar krijgt --p-header-bar-blur=0 en alpha=0 zodat het
 *   chrome-effect verdwijnt — identiek aan menu-open
 * - de header-toggle (`[data-menu-toggle]`) sluit het filter wanneer dat
 *   open is; we kapen de click in capture-fase voordat header.js het ziet
 * - form-state is instant-apply: elke input-change dispatcht een event
 *   waar archive-vacature.js op luistert om de ajax te triggeren
 */

function initFilterDrawer() {
	if ( ! CustomEase.get( 'energyFilter' ) ) {
		CustomEase.create( 'energyFilter', 'M0,0 C0.32,0.72 0,1 1,1' );
	}

	const drawer    = document.querySelector( '[data-filter-drawer]' );
	const panel     = document.querySelector( '[data-filter-drawer-panel]' );
	const overlayEl = document.querySelector( '[data-filter-drawer-overlay]' );
	const triggers  = document.querySelectorAll( '[data-filter-drawer-trigger]' );
	const shellEls  = Array.from( document.querySelectorAll( '[data-shell]' ) );

	// Header-chrome (gedeeld met menu): we lezen dezelfde DOM-knooppunten
	// als header.js zodat de animaties pixel-identiek lopen.
	const headerEl   = document.querySelector( '.p-header' );
	const toggleBtn  = document.querySelector( '[data-menu-toggle]' );
	const iconMenu   = toggleBtn && toggleBtn.querySelector( '.p-header__toggleSvg--menu' );
	const iconClose  = toggleBtn && toggleBtn.querySelector( '.p-header__toggleSvg--close' );
	const toggleLabels = toggleBtn ? toggleBtn.querySelectorAll( '.p-header__toggleLabel' ) : [];

	if ( ! drawer || ! panel || ! overlayEl || ! shellEls.length || ! toggleBtn || ! headerEl ) {
		return;
	}

	const largeItems     = panel.querySelectorAll( '[data-drawer-reveal-large]' );
	const smallItems     = panel.querySelectorAll( '[data-drawer-reveal-small]' );
	const darkEl         = overlayEl.querySelector( '.p-filterDrawer__dark' );
	const corners        = overlayEl.querySelectorAll( '.p-filterDrawer__corner' );
	const overlayBorders = overlayEl.querySelectorAll( '.p-filterDrawer__borderRow' );

	const form = panel.querySelector( '[data-filter-drawer-form]' );
	const submit = panel.querySelector( '.p-filterDrawer__submit' );
	if ( submit ) submit.hidden = true; // JS aanwezig → no-script fallback verbergen

	let isOpen = false;
	let tl;
	let enterEndTime = 0;

	const getOffset = () => -panel.offsetWidth;

	// Panel in rust verbergen: het deelt z-index 1 met het hoofdmenu-panel en
	// staat later in de DOM, dus zonder dit schuift het wit óver het geopende
	// menu heen op het archief. Verbergen haalt de formuliervelden ook uit de
	// tab-volgorde zolang de drawer dicht is.
	gsap.set( [ overlayEl, panel ], { visibility: 'hidden', pointerEvents: 'none' } );
	gsap.set( darkEl, { autoAlpha: 0 } );
	gsap.set( corners, { scale: 0 } );
	if ( overlayBorders[ 0 ] ) gsap.set( overlayBorders[ 0 ], { yPercent: -100 } );
	if ( overlayBorders[ 1 ] ) gsap.set( overlayBorders[ 1 ], { yPercent: 100 } );

	function buildTimeline() {
		tl = gsap.timeline( {
			paused: true,
			defaults: {
				ease: 'energyFilter',
				easeReverse: 'power2.inOut',
			},
		} );

		tl.set( [ overlayEl, panel ], { visibility: 'visible', pointerEvents: 'auto' }, 0 );

		// --- Shared chrome (gespiegeld aan menu) -----------------------------
		tl.to( [ ...shellEls, overlayEl ], {
			x: getOffset,
			duration: 0.7,
		}, 0 )

		.to( headerEl, {
			'--p-header-bar-blur': '0px',
			'--p-header-bar-bg-alpha': 0,
			duration: 0.4,
		}, 0 )

		.to( darkEl, {
			autoAlpha: 1,
			duration: 0.5,
		}, 0 )

		.to( corners, {
			scale: 1,
			duration: 0.5,
		}, 0 )

		.to( overlayBorders, {
			yPercent: 0,
			duration: 0.5,
		}, 0 );

		// Toggle-icon swap (menu icon → close icon)
		if ( iconMenu ) {
			tl.to( iconMenu, {
				autoAlpha: 0,
				scale: 0.6,
				rotation: 90,
				duration: 0.3,
				ease: 'power2.in',
				easeReverse: 'power2.out',
			}, 0 );
		}
		if ( iconClose ) {
			tl.to( iconClose, {
				autoAlpha: 1,
				scale: 1,
				rotation: 0,
				duration: 0.35,
				ease: 'back.out(1.4)',
				easeReverse: 'power2.in',
			}, 0.15 );
		}
		if ( toggleLabels.length ) {
			tl.to( toggleLabels, {
				yPercent: -100,
				duration: 0.4,
			}, 0 );
		}

		// --- Filter-specifieke item-reveal ----------------------------------
		tl.fromTo( largeItems,
			{ autoAlpha: 0, xPercent: 25 },
			{
				autoAlpha: 1,
				xPercent: 0,
				duration: 0.7,
				stagger: 0.05,
			},
			0
		);

		if ( smallItems.length ) {
			tl.fromTo( smallItems,
				{ autoAlpha: 0, yPercent: 100 },
				{
					autoAlpha: 1,
					yPercent: 0,
					duration: 0.5,
					stagger: 0.03,
					ease: 'power3.out',
				},
				0.3
			);
		}

		enterEndTime = tl.duration();

		tl.addPause();

		// --- Reverse leg -----------------------------------------------------
		tl.to( [ largeItems, smallItems ], {
			autoAlpha: 0,
			duration: 0.3,
		}, '<' )

		.to( [ ...shellEls, overlayEl ], {
			x: 0,
			duration: 0.6,
		}, '<' )

		.to( headerEl, {
			'--p-header-bar-blur': '5px',
			'--p-header-bar-bg-alpha': 0.36,
			duration: 0.3,
		}, '<' )

		.to( darkEl, {
			autoAlpha: 0,
			duration: 0.35,
			ease: 'power2.inOut',
		}, '<' )

		.to( corners, {
			scale: 0,
			duration: 0.5,
		}, '<' );

		if ( overlayBorders[ 0 ] ) {
			tl.to( overlayBorders[ 0 ], {
				yPercent: -100,
				duration: 0.5,
			}, '<' );
		}
		if ( overlayBorders[ 1 ] ) {
			tl.to( overlayBorders[ 1 ], {
				yPercent: 100,
				duration: 0.5,
			}, '<' );
		}

		if ( toggleLabels.length ) {
			tl.to( toggleLabels, {
				yPercent: 0,
				duration: 0.25,
				ease: 'power3.in',
			}, '<' );
		}

		if ( iconClose ) {
			tl.to( iconClose, {
				autoAlpha: 0,
				scale: 0.6,
				rotation: -90,
				duration: 0.25,
				ease: 'power2.in',
			}, '<' );
		}

		if ( iconMenu ) {
			tl.to( iconMenu, {
				autoAlpha: 1,
				scale: 1,
				rotation: 0,
				duration: 0.3,
				ease: 'back.out(1.4)',
			}, '<+=0.1' );
		}

		tl.set( [ overlayEl, panel ], {
			visibility: 'hidden',
			pointerEvents: 'none',
		} );
	}

	function setOpen( next ) {
		if ( next === isOpen ) return;
		isOpen = next;
		drawer.setAttribute( 'aria-hidden', isOpen ? 'false' : 'true' );
		document.body.setAttribute( 'data-filter-drawer-status', isOpen ? 'open' : '' );
		document.body.style.overflow = isOpen ? 'hidden' : '';
		toggleBtn.setAttribute( 'aria-expanded', String( isOpen ) );
		toggleBtn.setAttribute( 'aria-label', isOpen ? 'Sluit filter' : 'Open menu' );

		if ( isOpen ) {
			tl.invalidate();
			if ( tl.time() >= enterEndTime ) {
				tl.timeScale( 1 ).restart();
			} else {
				tl.timeScale( 1 ).play();
			}
		} else {
			tl.timeScale( 1.2 ).play();
		}
	}

	function open() { setOpen( true ); }
	function close() { setOpen( false ); }

	buildTimeline();

	triggers.forEach( ( btn ) => btn.addEventListener( 'click', ( e ) => {
		e.preventDefault();
		open();
	} ) );

	// Overlay-click sluit het paneel (data-filter-drawer-close marker).
	drawer.querySelectorAll( '[data-filter-drawer-close]' ).forEach( ( el ) => {
		el.addEventListener( 'click', ( e ) => {
			e.preventDefault();
			close();
		} );
	} );

	// Header-toggle = gedeelde close: in capture-fase onderscheppen voordat
	// header.js zijn eigen toggle-handler runt.
	toggleBtn.addEventListener( 'click', ( e ) => {
		if ( isOpen ) {
			e.stopImmediatePropagation();
			e.preventDefault();
			close();
		}
	}, true );

	document.addEventListener( 'keydown', ( e ) => {
		if ( e.key === 'Escape' && isOpen ) close();
	} );

	// --- Form-state helpers ----------------------------------------------
	if ( form ) {
		const range = panel.querySelector( '[data-filter-drawer-range]' );
		const rangeOutput = panel.querySelector( '[data-filter-drawer-range-output]' );

		const updateRangeOutput = () => {
			if ( range && rangeOutput ) {
				rangeOutput.textContent = `${ range.value } uur`;
			}
		};
		if ( range ) range.addEventListener( 'input', updateRangeOutput );

		// Instant-apply: elke change dispatcht een custom event op de form
		// die archive-vacature.js opvangt om de ajax-fetch te triggeren.
		const dispatchChange = () => {
			form.dispatchEvent( new CustomEvent( 'filterdrawer:change', { bubbles: true } ) );
		};

		// Checkboxes/radios → direct op change
		form.addEventListener( 'change', ( e ) => {
			if ( e.target.matches( 'input[type="checkbox"], input[type="radio"]' ) ) {
				dispatchChange();
			}
		} );

		// Range slider → debounce 200ms na laatste input
		if ( range ) {
			let t;
			range.addEventListener( 'input', () => {
				window.clearTimeout( t );
				t = window.setTimeout( dispatchChange, 200 );
			} );
		}

		// Reset/Toon alles
		const reset = panel.querySelector( '[data-filter-drawer-reset]' );
		if ( reset ) {
			reset.addEventListener( 'click', () => {
				form.reset();
				if ( range ) range.value = 0;
				updateRangeOutput();
				// Volledige reset: ook q en cat clearen via een aparte event.
				form.dispatchEvent( new CustomEvent( 'filterdrawer:reset', { bubbles: true } ) );
			} );
		}
	}

	window.addEventListener( 'resize', () => {
		if ( ! isOpen ) return;
		const offset = getOffset();
		gsap.set( [ ...shellEls, overlayEl ], { x: offset } );
	} );
}

document.addEventListener( 'DOMContentLoaded', initFilterDrawer );
